import { Element, useEditor, useNode } from "@craftjs/core";
import { Popover } from "@headlessui/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Schedule } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import BackgroundSquiggle from "@/components/editor/shared/background-squiggle";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
} from "@/components/editor/shared/block-wrapper";
import EmptyItem from "@/components/editor/shared/schedules/empty-item-component";
import Filter from "@/components/editor/shared/schedules/filter-component";
import { Notification } from "@/components/editor/shared/schedules/notification-component";
import PartsTagsDisplay from "@/components/editor/shared/schedules/parts-tags-display-component";
import ScheduleSettings from "@/components/editor/shared/schedules/schedule-settings/schedule-settings-component";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@/components/editor/shared/schedules/schedule-tab";
import useTheme from "@/components/editor/themes/use-theme";
import { useScheduleFilter } from "@/hooks/use-schedule-filter";
import { useSchedulesWithEventTimezone } from "@/hooks/use-schedules-with-event-timezone";
import useViewport from "@/hooks/use-viewport";

import {
  FilterWrapper,
  PositionedWrapper,
  StyledMenuContent,
  Wrapper,
} from "./default-schedule-component-styles";
import DefaultScheduleDisplay from "./default-schedule-display-component";
import { SearchInput } from "./search-input";

type DefaultScheduleProps = {
  schedules: Schedule[];
};

export default function DefaultSchedule(props: DefaultScheduleProps) {
  const {
    id: nodeId,
    connectors: { connect },
  } = useNode();

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const viewport = useViewport();
  const isMobile = React.useMemo(() => viewport === "mobile", [viewport]);

  const {
    searchValue,
    setSearchValue,
    selectedTags,
    handleTagSelection,
    selectedParts,
    handlePartSelection,
    clearAllSelections,
    isMenuOpen,
    toggleMenu,
    menuRef,
    filters,
    schedules,
  } = useScheduleFilter(props.schedules);

  const schedulesWithEventTimezone = useSchedulesWithEventTimezone(schedules);

  const renderSearchBar = React.useCallback(
    () => (
      <SearchInput
        aria-label={t("schedule.search-segments")}
        label={t("schedule.search-segments")}
        onClear={() => setSearchValue("")}
        onChange={setSearchValue}
        value={searchValue}
      />
    ),
    [searchValue, setSearchValue, t]
  );

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={nodeId}
      data-testid="schedule-component"
    >
      <Wrapper>
        <BlockMaxWidthWrapper>
          <div className="flex justify-between">
            <Element
              id="title"
              is={Text}
              text="Schedule highlights"
              element="h2"
              pattern="headingTwo"
            />
            <div className="ml-2 flex items-start">
              <FilterWrapper>
                <Popover as="div" className="filter-menu" ref={menuRef}>
                  <Filter
                    id="filter-options"
                    label="Filter"
                    totalFiltersSelected={
                      selectedTags.length + selectedParts.length
                    }
                    onClick={toggleMenu}
                    isToggled={isMenuOpen}
                  />
                  {isMenuOpen && (
                    <StyledMenuContent
                      $rounded={!isMobile}
                      $withBorder={!isMobile}
                      $editing={enabled}
                      role="menu"
                      aria-labelledby="filter-options"
                    >
                      <PartsTagsDisplay
                        onClickPart={handlePartSelection}
                        onClickTag={handleTagSelection}
                        parts={filters.partOptions}
                        selectedParts={selectedParts}
                        selectedTags={selectedTags}
                        tags={filters.tagOptions}
                        onClose={toggleMenu}
                        onClear={clearAllSelections}
                        rounded
                      />
                    </StyledMenuContent>
                  )}
                </Popover>
              </FilterWrapper>
              {!isMobile && renderSearchBar()}
            </div>
          </div>
          <Tabs defaultIndex={0}>
            <TabList>
              {schedulesWithEventTimezone.map((schedule) => (
                <Tab key={schedule.date}>
                  {schedule.date}
                  {!schedule.isNoFilters && (
                    <Notification
                      className={currentTheme.kind}
                      aria-label={t("schedule.total-search-results", {
                        total_search: schedule.count,
                      })}
                      role="alert"
                    >
                      {schedule.count}
                    </Notification>
                  )}
                </Tab>
              ))}
            </TabList>
            {isMobile && renderSearchBar()}
            <PositionedWrapper>
              <BackgroundSquiggle />
              <TabPanels>
                {schedulesWithEventTimezone.map((schedule) => (
                  <TabPanel key={schedule.date}>
                    {schedule.count === 0 ? (
                      <div className="py-6">
                        <EmptyItem
                          includesSearch={!!searchValue}
                          partsCount={selectedParts.length}
                          tagsCount={selectedTags.length}
                        />
                      </div>
                    ) : (
                      <DefaultScheduleDisplay schedules={schedule.items} />
                    )}
                  </TabPanel>
                ))}
              </TabPanels>
            </PositionedWrapper>
          </Tabs>
        </BlockMaxWidthWrapper>
      </Wrapper>
    </BlockBackgroundColorWrapper>
  );
}

DefaultSchedule.craft = {
  custom: {
    displayName: "Default Schedule",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: ScheduleSettings,
  },
};
